import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { JsonService } from '../_services/json.service';
import { ConfirmationDialogService } from '../_services/confirmation-dialog.service';
import { DialogService } from '../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { StringSwitchService } from '../_helpers/string-switch.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { GlobalService } from '../_services/global.service';
import { SharedService } from '../_services/shared.service';

@Component({
  selector: 'app-listagem-pending-issues',
  templateUrl: './listagem-pending-issues.component.html',
  styleUrls: ['./listagem-pending-issues.component.scss']
})
export class ListagemPendingIssuesComponent implements OnInit {
  listaItens: any[] = [];
  listaShowItens: any[] = [];
  allLoadedItems: any[] = [];
  private allItemsLoaded = false;

  currentPage = 1;
  pageSize = 15;
  totalItems = 0;

  listaStatus: any[] = [];
  listaRevisores: string[] = [];
  listaPaises: any[] = [];
  listaCidades: any[] = [];
  listaPDVs: any[] = [];
  listaAno: any[] = [];
  listaMes: any[] = [];
  listaRegiao: any[] = [];
  listaEnvio: any[] = [];
  listaProvedor: any[] = [];
  listaGrupos: any[] = [];
  jsonPaises: any[] = [];

  itensSelecionados: any[] = [];
  selecionarTodosShow = false;
  isModalOpen = false;
  isAdminUser = false;
  loading: boolean = false;

  isGerarCsvModalOpen = false;
  isGerarCsvResultModalOpen = false;
  gerarCsvMes = '';
  gerarCsvCountryCode = '';
  gerarCsvContagem: number | null = null;
  gerarCsvResultados: any[] = [];
  readonly listaMesesGeracaoCsv = [
    { label: 'Janeiro',   value: '01_ENERO' },
    { label: 'Fevereiro', value: '02_FEBRERO' },
    { label: 'Março',     value: '03_MARZO' },
    { label: 'Abril',     value: '04_ABRIL' },
    { label: 'Maio',      value: '05_MAYO' },
    { label: 'Junho',     value: '06_JUNIO' },
    { label: 'Julho',     value: '07_JULIO' },
    { label: 'Agosto',    value: '08_AGOSTO' },
    { label: 'Setembro',  value: '09_SEPTIEMBRE' },
    { label: 'Outubro',   value: '10_OCTUBRE' },
    { label: 'Novembro',  value: '11_NOVIEMBRE' },
    { label: 'Dezembro',  value: '12_DICIEMBRE' },
  ];

  // filenameFiltrado: string[] = []; // oculto temporariamente
  pdvFiltrado: string[] = [];

  // searchControl = new FormControl(); // oculto temporariamente
  pdvControl = new FormControl();
  filenameImagemControl = new FormControl();

  filtro: any = {
    status: 'P', usuario: '', data: '', pais: '', cidade: '',
    pdv: '', ano: '', mes: '', regiao: '', provedor: '',
    envio: '', grupos: '', selecionarTodos: '', responsavel: '', filename: '', revisado: ''
  };

  @ViewChild('csv_buttons', { static: true }) csv_buttons!: ElementRef<HTMLDivElement>;

  constructor(
    private jsonService: JsonService,
    private confirmationDialog: ConfirmationDialogService,
    private dialogService: DialogService,
    private router: Router,
    private loadingService: LoadingService,
    private stringSwitchService: StringSwitchService,
    private translate: TranslateService,
    private logger: LoggerService,
    private globalService: GlobalService,
    private sharedService: SharedService
  ) { }

  ngOnInit(): void {
    this.loadingService.show();
    this.restoreFiltro();

    this.isAdminUser = this.globalService.isAdmin();
    this.sharedService.isAdmin$.subscribe(v => this.isAdminUser = v);

    this.jsonService.unlockAllPage().subscribe({
      next: () => this.loadingService.hide(),
      error: err => { this.logger.error('Erro ao desbloquear página', err); this.loadingService.hide(); }
    });

    this.loadFilters();
    // this.setupSearch(this.searchControl, term => this.jsonService.getSearchFilename(term), res => this.filenameFiltrado = res); // oculto temporariamente
    this.setupSearch(this.pdvControl, term => this.jsonService.getSearchPdv(term), res => this.pdvFiltrado = res);
    this.filenameImagemControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(value => {
      this.filtro.filename = value || '';
    });

    this.atualizaFiltro(true);
  }

  private setupSearch(control: FormControl, serviceFn: (term: string) => any, updateFn: (res: any) => void) {
    control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => !term?.trim() ? of([]) : serviceFn(term).pipe(
        tap((resp: any) => {
          updateFn(resp);
        }),
        catchError(err => {
          this.logger.error('Erro na busca', err);
          return of([]);
        })
      ))
    ).subscribe();
  }

  private restoreFiltro() {
    const saved = localStorage.getItem('filtro');
    this.filtro = saved ? JSON.parse(saved) : this.filtro;
    if (!saved) localStorage.setItem('filtro', JSON.stringify(this.filtro));
  }

  loadPage(_page: number): void {
    const params = this.buildFilterParams()
      .append('PageNumber', 1)
      .append('pageSize', 9999);

    this.jsonService.getListPrescriptionsPendentes(params).subscribe({
      next: resp => {
        this.allLoadedItems = resp.items;
        this.allItemsLoaded = true;
        this.totalItems = resp.totalItems;
        this.listaRevisores = [...new Set(
          resp.items.map((item: any) => item.revisado).filter((r: any): r is string => !!r)
        )];
        this.currentPage = 1;
        this.aplicarFiltroRevisor(resp.items);
        this.loadingService.hide();
      },
      error: err => this.logger.error('Erro ao carregar página', err)
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
  }

  editarReceita(item: any) {
    if (item.sendoAcessado) {
      this.dialogService.abrirDialog('aviso', this.t('Aviso'), this.t('Receita já está sendo acessada por outro usuário!'));
      return;
    }
    localStorage.setItem('filtro', JSON.stringify(this.filtro));
    const formData = new FormData();
    formData.append('status', '1');
    formData.append('prescriptionId', item.id);
    this.jsonService.lockPageCreate(formData).subscribe({
      next: () => this.router.navigate(
        ['/receita-pending-issues', item.id, item.regiao],
        { state: { filtro: this.filtro } }
      ),
      error: err => this.logger.error('Erro ao editar receita', err)
    });
  }

  sendCaptura() {
    this.isModalOpen = false;
    this.loadingService.show();
    this.jsonService.sendCaptura().subscribe({
      next: resp => this.handleCapturaResponse(resp),
      error: err => this.showError('Erro ao enviar captura', err)
    });
  }

  private handleCapturaResponse(resp: any) {
    if (!resp.isSuccess) {
      this.dialogService.abrirDialog('aviso', this.t('Sucesso'), this.t('¡Captura completada con éxito! Los arquivos se crearán pronto.'));
      resp.forEach((item: any) => this.downloadFile(item['pais'], item['bytes']));
    } else {
      this.dialogService.abrirDialog('aviso', this.t('Error'), this.t('Nenhuma prescrição foi revisada nas últimas 24 horas.'));
    }
    this.loadingService.hide();
  }

  private showError(msg: string, err: any) {
    this.logger.error(msg, err);
    this.dialogService.abrirDialog('aviso', this.t('Error'), this.t('Ocorreu um erro inesperado, favor tentar novamente!'));
    this.loadingService.hide();
  }

  downloadFile(titulo: string, bytesBase64: string, filenameOverride?: string) {
    try {
      const clean = bytesBase64.replace(/\s/g, '').replace(/[^A-Za-z0-9+/=]/g, '');
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
      const text = new TextDecoder('utf-8').decode(bytes);
      const sanitized = this.sanitizeCsvPipes(text);
      const blob = new Blob([sanitized], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filenameOverride || `${titulo}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (e) {
      this.logger.error('Erro ao processar Base64', e);
    }
  }

  private sanitizeCsvPipes(text: string): string {
    const lines = text.split(/\r?\n/);
    if (!lines.length) return text;
    const headerPipes = (lines[0].match(/\|/g) ?? []).length;
    return lines.map((line, i) => {
      if (i === 0 || !line.trim()) return line;
      const linePipes = (line.match(/\|/g) ?? []).length;
      if (linePipes <= headerPipes) return line;
      let seen = 0;
      return [...line].map(ch => {
        if (ch !== '|') return ch;
        return ++seen <= headerPipes ? '|' : '';
      }).join('');
    }).join('\n');
  }

  private loadFilters() {
    forkJoin({
      paises: this.jsonService.listCountry(),
      pdvs: this.jsonService.getPDVs(),
      anos: this.jsonService.getAno(),
      meses: this.jsonService.getMes(),
      regioes: this.jsonService.getRegiao(),
      provedores: this.jsonService.getProvedor(),
      envios: this.jsonService.getEnvio(),
      grupos: this.jsonService.getGroupList(),
      status: this.jsonService.getStatus()
    }).subscribe({
      next: data => {
        this.listaPaises = data.paises;
        this.listaPDVs = data.pdvs;
        this.listaAno = data.anos;
        this.listaMes = data.meses;
        this.listaRegiao = data.regioes;
        this.listaProvedor = data.provedores;
        this.listaEnvio = data.envios;
        this.listaGrupos = data.grupos;
        this.listaStatus = data.status.filter((s: any) => s !== 'W' && s !== 'E');
        this.jsonPaises = data.paises;
      },
      error: err => this.logger.error('Erro ao carregar filtros', err)
    });
  }

  private buildFilterParams(): HttpParams {
    const uiOnlyFields = new Set(['selecionarTodos', 'grupos', 'revisado']);
    let p = new HttpParams();
    Object.entries(this.filtro).forEach(([k, v]) => {
      if (uiOnlyFields.has(k)) return;
      if (v === '' || v === null || v === undefined) return;
      p = p.append(k === 'regiao' ? 'region' : k, String(v));
    });
    return p;
  }


  private aplicarFiltroRevisor(items: any[]): void {
    this.listaShowItens = this.filtro.revisado
      ? items.filter(item => item.revisado === this.filtro.revisado)
      : [...items];
  }

  onRevisorChange(): void {
    localStorage.setItem('filtro', JSON.stringify(this.filtro));
    if (!this.filtro.revisado || this.allItemsLoaded) {
      this.currentPage = 1;
      this.aplicarFiltroRevisor(this.allLoadedItems);
      return;
    }
    // Background ainda em andamento: aguarda com spinner e usa carregarTodosRevisores
    this.loadingService.show();
    const params = this.buildFilterParams()
      .append('PageNumber', 1)
      .append('pageSize', this.totalItems);
    this.jsonService.getListPrescriptionsPendentes(params).subscribe({
      next: resp => {
        this.allLoadedItems = resp.items;
        this.allItemsLoaded = true;
        this.listaRevisores = [...new Set(
          resp.items.map((item: any) => item.revisado).filter((r: any): r is string => !!r)
        )];
        this.currentPage = 1;
        this.aplicarFiltroRevisor(this.allLoadedItems);
        this.loadingService.hide();
      },
      error: err => { this.logger.error('Erro ao carregar revisores', err); this.loadingService.hide(); }
    });
  }

  private hasOtherActiveFilter(): boolean {
    const skipFields = new Set(['status', 'selecionarTodos', 'grupos', 'revisado', 'dataf', 'data']);
    return Object.entries(this.filtro).some(([k, v]) =>
      !skipFields.has(k) && v !== '' && v !== null && v !== undefined
    );
  }

  atualizaFiltro(silent: boolean = false) {
    this.filtro.data = this.formatDate(this.filtro.dataf);
    localStorage.setItem('filtro', JSON.stringify(this.filtro));
    this.allItemsLoaded = false;

    // Admin pleno vê todas as receitas sem restrição de grupo — exige país pra evitar
    // consulta irrestrita sobre a base inteira. `silent` evita o aviso na carga inicial
    // da tela (ngOnInit), onde ainda não há país selecionado — só o clique em "Consultar"
    // (ou uma busca restaurada do localStorage já com país) deve avisar.
    if (this.isAdminUser && !this.filtro.country) {
      if (!silent) {
        this.dialogService.abrirDialog(
          'aviso',
          this.translate.instant('Aviso') + '!',
          this.translate.instant('Selecione um país para realizar a pesquisa.')
        );
      }
      return;
    }

    if (!this.filtro.status && !this.hasOtherActiveFilter()) {
      this.dialogService.abrirDialog(
        'aviso',
        this.translate.instant('Aviso') + '!',
        this.translate.instant('Selecione pelo menos um filtro além do status para carregar os dados.')
      );
      return;
    }

    this.loadingService.show();
    this.loadPage(1);
    this.atualizaSelecionado();
  }

  limparFiltro() {
    localStorage.removeItem('filtro');
    this.filtro = { status: '', usuario: '', data: '', pais: '', cidade: '', pdv: '', ano: '', mes: '', regiao: '', provedor: '', envio: '', grupos: '', selecionarTodos: '', responsavel: '', filename: '', revisado: '' };
    this.filenameImagemControl.setValue('', { emitEvent: false });
    this.allLoadedItems = [];
    this.listaShowItens = [];
    this.listaRevisores = [];
    this.totalItems = 0;
    this.currentPage = 1;
    this.atualizaSelecionado();
  }

  // Helpers
  private t(key: string) { return this.translate.instant(key) + '!'; }
  formatDate(date: string): string { return date ? date.split('-').reverse().join('/') : ''; }
  switchString(value: string) { return this.stringSwitchService.switchString(value); }

  arredondar(valor: number): number {
    return Math.ceil(valor);
  }
  getCountry(countryId: string): string {
    if (!this.listaPaises || this.listaPaises.length === 0) return countryId;
    const pais = this.listaPaises.find(p => p.id === countryId);
    return pais ? pais.name : countryId;
  }

  getRegionName(regionId: string): string {
    if (!this.listaRegiao || this.listaRegiao.length === 0) return regionId;
    const regiao = this.listaRegiao.find(r => r.id === regionId);
    return regiao ? regiao.name : regionId;
  }
  retornaGroupName(groupId: string): string {
    if (!this.listaGrupos || this.listaGrupos.length === 0) {
      return groupId;
    }

    const grupo = this.listaGrupos.find(g => g.id === groupId);
    return grupo ? grupo.name : groupId;
  }

  toggleSelecionarTodos() {
    this.selecionarTodosShow = !this.selecionarTodosShow;

    if (this.selecionarTodosShow) {
      this.listaShowItens.forEach(item => item.selecionado = true);
    } else {
      this.listaShowItens.forEach(item => item.selecionado = false);
    }

    this.atualizaSelecionado();
  }

  atualizaSelecionado() {
    this.itensSelecionados = this.listaShowItens.filter(item => item.selecionado);
    if(this.itensSelecionados.length <= 0) {
      this.selecionarTodosShow = false;
    }else{
      this.selecionarTodosShow = true;
    }
    console.log(this.itensSelecionados);
  }

  openModal(): void {
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  abrirModalGerarCsv(): void {
    this.gerarCsvMes = '';
    this.gerarCsvCountryCode = '';
    this.gerarCsvContagem = null;
    this.isGerarCsvModalOpen = true;
  }

  fecharModalGerarCsv(): void {
    this.isGerarCsvModalOpen = false;
  }

  onFiltroGerarCsvChange(): void {
    this.gerarCsvContagem = null;
    if (!this.gerarCsvMes) return;

    this.jsonService.previewGenerateCsvByCountryPeriod(this.gerarCsvMes, this.gerarCsvCountryCode || undefined).subscribe({
      next: resp => {
        this.gerarCsvContagem = (resp || []).reduce((total: number, item: any) => total + (item.prescriptionCount || 0), 0);
      },
      error: err => this.logger.error('Erro ao consultar quantidade de receitas', err)
    });
  }

  confirmarGerarCsv(): void {
    if (!this.gerarCsvMes) return;

    this.isGerarCsvModalOpen = false;
    this.loadingService.show();
    this.jsonService.generateCsvByCountryPeriod(this.gerarCsvMes, this.gerarCsvCountryCode || undefined).subscribe({
      next: resp => this.handleGerarCsvResponse(resp),
      error: err => this.showError('Erro ao gerar arquivo', err)
    });
  }

  private handleGerarCsvResponse(resp: any[]): void {
    this.loadingService.hide();
    const gerados = (resp || []).filter(item => item.isSuccess);
    if (gerados.length > 0) {
      this.gerarCsvResultados = gerados;
      this.isGerarCsvResultModalOpen = true;
    } else {
      this.dialogService.abrirDialog('aviso', this.t('Aviso'), this.t('Nenhuma receita encontrada para o período informado.'));
    }
  }

  fecharModalResultadoGerarCsv(): void {
    this.isGerarCsvResultModalOpen = false;
    this.gerarCsvResultados = [];
  }


  async changeGroup() {
    this.itensSelecionados = this.listaShowItens.filter(item => item.selecionado);
    if(this.itensSelecionados.length >= 1) {
      this.selecionarTodosShow = true;
    }else{
      this.selecionarTodosShow = false;
    }
    const selectedItems = this.itensSelecionados;
    let finalArray : any = {};
    let itensArray : Array<any> = [];
    selectedItems.forEach(item => {
      itensArray.push(item.id);
    });
    finalArray['newGroupId'] = this.filtro.grupos;
    finalArray['prescriptionIds'] = itensArray;
    const confirmed = await this.confirmationDialog.confirm(
      'Confirmação',
      'Tem certeza de que deseja alterar o grupo destes itens?'
    );

    if (confirmed) {
      this.loadingService.show();
      this.chageGroupSend(finalArray);
    } else {
      //
    }      
  }

  chageGroupSend(array: any) {
    this.jsonService.changeGroup(array).subscribe({
      next: () => {
        this.loadingService.hide();
        const title = this.translate.instant('Sucesso') + '!';
        const texto = this.translate.instant('Receita publicada para análise!');
        this.dialogService.abrirDialog('aviso', title, texto);
        setTimeout(() => window.location.reload(), 2000);
      },
      error: erro => this.logger.error('Erro ao alterar grupo', erro)
    });
  }

  async atualizaResponsavel(item: any) {
    let finalArray : any = {};
    let itensArray : Array<any> = [item.id];
    finalArray['newGroupId'] = item.groupId;
    finalArray['prescriptionIds'] = itensArray;

    const confirmed = await this.confirmationDialog.confirm(
      'Confirmação',
      'Tem certeza de que deseja alterar o grupo deste item?'
    );

    if (confirmed) {
      this.chageGroupSend(finalArray);
    } else {
      //
    }
    
  }
}
